import { Box, Chip, Typography, Button, Stack, Tooltip } from "@mui/material";
import {
  PartyRecordSettlementType,
  type SettlementDto,
} from "Infrastructure/Api/Api";
import { formatCurrency } from "Utils/CurrencyUtils";
import { formatDate } from "Utils/DateUtils";
import { formatPeriod, PAYMENT_STATUS_TO_INFO } from "Utils/SettlementUtils";
import { useUpdatePartySettlementRecordStatus } from "Api/Mutations/Settlements/useUpdatePartySettlementRecordStatus";
import { toast } from "sonner";
import { useIsCurrentUserAdmin } from "Hooks/useIsCurrentUserAdmin";
import { appUrl } from "Utils/UrlUtils";
import { useIsCurrentUserMerchant } from "Hooks/useIsCurrentUserMerchant";
import { useNavigate } from "Navigation/useNavigate";
import { useDownloadSettlementReportMutation } from "Hooks/useSettlementReportDownload";
import { useDownloadSettlementInvoiceMutation } from "Api/Mutations/Invoices/useDownloadSettlementInvoiceMutation";
import { useRegenerateSettlementInvoiceMutation } from "Api/Mutations/Invoices/useRegenerateSettlementInvoiceMutation";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import type { SettlementUserRole } from "Types/settlements";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.Settlements.SettlementList;

type Props = {
  settlement: SettlementDto;
  previousSettlementDate?: string | null;
  partyPublicID?: string;
  userRole: SettlementUserRole;
  isReadOnly?: boolean;
};

export const SettlementListItem = ({
  settlement,
  previousSettlementDate,
  partyPublicID = "",
  userRole,
  isReadOnly = false,
}: Props) => {
  const navigate = useNavigate();
  const isAdmin = useIsCurrentUserAdmin();
  const isMerchant = useIsCurrentUserMerchant();
  const { t } = useResource();

  const { mutate: updateSettlementStatus, isPending } =
    useUpdatePartySettlementRecordStatus({
      onSuccess: () => {
        toast.success(t(COMPONENT_RESOURCES.UpdateStatusSuccess));
      },
      onError: () => {
        toast.error(t(COMPONENT_RESOURCES.UpdateStatusError));
      },
    });

  const { mutate: downloadReport, isPending: isDownloadPending } =
    useDownloadSettlementReportMutation();

  const { mutate: downloadInvoice, isPending: isInvoiceDownloadPending } =
    useDownloadSettlementInvoiceMutation();

  const { mutate: regenerateInvoice, isPending: isInvoiceRegeneratePending } =
    useRegenerateSettlementInvoiceMutation();

  const {
    amount,
    currencyCode,
    type,
    settlementDate,
    dueDate,
    isPaid,
    settlementID,
    transaction,
    invoiceNumber,
    settlementPeriodID,
    settlementStatus,
    region,
  } = settlement;

  const { label, color } = PAYMENT_STATUS_TO_INFO[settlementStatus];

  const markAsPaid = () => {
    updateSettlementStatus({ settlementID, isPaid: true });
  };

  const redirectToPayment = () => {
    if (transaction?.publicID) {
      navigate(`${appUrl("customer-payment-pay")}/${transaction.publicID}`);
    }
  };

  const settlementTypeText =
    type === PartyRecordSettlementType.HunterCommissionFunding
      ? t(COMPONENT_RESOURCES.SettlementTypeHunterCommissionFunding)
      : t(COMPONENT_RESOURCES.SettlementTypeDefault);

  return (
    <Box
      sx={({ spacing, palette }) => ({
        borderRadius: spacing(2),
        border: `1px solid ${palette.grey[300]}`,
        p: 2,
      })}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight="medium">
            {t(COMPONENT_RESOURCES.SettlementPeriod)}{" "}
            {formatPeriod(settlementDate, previousSettlementDate)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {settlementTypeText}
          </Typography>
        </Box>
        <Tooltip
          arrow
          placement="bottom"
          title={t(COMPONENT_RESOURCES.SettlementStatusTooltip)}
        >
          <Chip
            label={label}
            color={color}
            size="small"
            sx={{ cursor: "default" }}
          />
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            {t(COMPONENT_RESOURCES.DueDate)}:
          </Typography>
          <Typography variant="body1">
            {dueDate ? formatDate(dueDate) : t(Resources.Common.UnknownDate)}
          </Typography>
        </Box>

        <Box sx={{ textAlign: "right" }}>
          <Typography variant="body2" color="text.secondary">
            {t(COMPONENT_RESOURCES.Amount)}:
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="primary">
            {formatCurrency({ amount, currencyCode })}
          </Typography>
        </Box>
      </Box>

      <Stack spacing={1} mt={2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          {settlementPeriodID && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() =>
                downloadReport({
                  partyPublicID,
                  settlementPeriodID,
                  userRole,
                  region,
                })
              }
              disabled={isDownloadPending}
              sx={{ flex: 1 }}
            >
              {t(COMPONENT_RESOURCES.DownloadReport)}
            </Button>
          )}
          {invoiceNumber != null && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ReceiptIcon />}
              onClick={() => downloadInvoice({ settlementID })}
              disabled={isInvoiceDownloadPending}
              sx={{ flex: 1 }}
            >
              {t(COMPONENT_RESOURCES.DownloadInvoice)}
            </Button>
          )}
          {invoiceNumber != null && isAdmin && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutorenewIcon />}
              onClick={() => regenerateInvoice({ invoiceNumber })}
              disabled={isInvoiceRegeneratePending}
              sx={{ flex: 1 }}
            >
              {t(COMPONENT_RESOURCES.RegenerateInvoice)}
            </Button>
          )}
        </Stack>
        {!isPaid && isAdmin && (
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={markAsPaid}
            disabled={isPending || isReadOnly}
            fullWidth
          >
            {t(COMPONENT_RESOURCES.MarkAsPaid)}
          </Button>
        )}
        {!isPaid && isMerchant && (
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={redirectToPayment}
            disabled={isPending || isReadOnly}
            fullWidth
          >
            {t(COMPONENT_RESOURCES.Pay)}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
